import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { pdfFileNameInterceptor } from 'app/core/http/pdf-file-name.interceptor';
import { ApplicationConfig, ErrorHandler, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, provideRouter, withInMemoryScrolling } from '@angular/router';
import { StaleBuildService } from 'app/core/version/stale-build.service';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { firstValueFrom } from 'rxjs';

import { appRoutes } from '@app/app.routes';
import { provideAuth } from '@app/core/auth/auth.provider';
import { BrandingService } from '@app/core/branding/branding.service';
import { API_CONFIG, apiKeyInterceptor, authInterceptor, duplicateCreateInterceptor, errorInterceptor } from '@app/core/http';
import { mailSessionInterceptor } from '@app/modules/admin/mail-automation/shared/mail-session';
import { provideIcons } from '@app/core/icons/icons.provider';
import { TranslocoHttpLoader } from '@app/core/transloco/transloco.http-loader';
import { MockApiService } from '@app/mock-api';
import { environment as env } from '@env';
import { provideFuse } from '@fuse';
import { FuseLoadingService } from '@fuse/services/loading';
import { trackNavigationLoading } from 'app/core/navigation-loading';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),

        provideHttpClient(withInterceptors([duplicateCreateInterceptor, mailSessionInterceptor, apiKeyInterceptor, authInterceptor, errorInterceptor, pdfFileNameInterceptor])),

        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

        {
            provide: API_CONFIG,
            useValue: {
                baseUrl: env.apiUrl,
            },
        },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'MM/dd/yyyy',
                },
                display: {
                    dateInput: 'MM/dd/yyyy',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({ showDialog: false }),
        },

        // Enable automatic performance instrumentation for routing
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);
            // Ensure TraceService is constructed early
            inject(Sentry.TraceService);
            inject(StaleBuildService); // reload stale tabs after an FE deploy
            trackNavigationLoading(inject(Router), inject(FuseLoadingService)); // ERP #385 loading bar on navbar clicks
            return Promise.all([
                firstValueFrom(translocoService.load(defaultLang)),
                inject(BrandingService).load(),
            ]);
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'compact',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
